import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Crown, ArrowRight, CheckCircle2 } from "lucide-react";
import { Container, FadeIn } from "@/components/Section";
import { IMG } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERKS = [
  "Priority access to drops",
  "Members-only experiences",
  "Discounts across the Academy",
  "Invitations to Kulture Lab open studios",
];

export default function MembershipWaitlist() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/membership-waitlist`, {
        email,
        name: name || null,
      });
      toast.success("You're on the membership list. We'll be in touch first.");
      setJoined(true);
      setEmail("");
      setName("");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail?.[0]?.msg ||
          "Could not join the waitlist. Try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      data-testid="membership-waitlist-section"
      className="relative py-28 sm:py-36 overflow-hidden"
    >
      <div
        className="absolute inset-0 bg-cover bg-center opacity-30"
        style={{ backgroundImage: `url(${IMG.xr})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black/85 to-black" />
      <Container className="relative">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <FadeIn className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 border border-amber-400/40 text-amber-400 text-[10px] uppercase tracking-[0.35em] font-bold mb-8">
              <Crown className="h-3.5 w-3.5" />
              Launching 2026
            </div>
            <h2 className="font-display font-black uppercase leading-[0.95] tracking-tighter text-4xl sm:text-5xl lg:text-6xl">
              TOK <br />
              <span className="text-amber-400">Membership.</span>
            </h2>
            <p className="mt-6 text-zinc-300 text-lg leading-relaxed max-w-md">
              A members' tier for the people who want to live inside our world.
              Join the waitlist for first access, founder pricing and exclusive
              experiences.
            </p>
            <ul className="mt-8 space-y-3">
              {PERKS.map((p) => (
                <li
                  key={p}
                  className="flex items-center gap-3 text-sm text-zinc-300"
                >
                  <CheckCircle2 className="h-4 w-4 text-amber-400 shrink-0" />
                  {p}
                </li>
              ))}
            </ul>
          </FadeIn>

          <FadeIn delay={0.15} className="lg:col-span-6">
            <div className="border border-amber-400/30 bg-zinc-950/80 backdrop-blur-md p-8 sm:p-10">
              {joined ? (
                <div data-testid="membership-joined" className="text-center py-6">
                  <CheckCircle2 className="h-12 w-12 text-amber-400 mx-auto" />
                  <p className="mt-6 font-display font-bold text-2xl">
                    You're on the list.
                  </p>
                  <p className="mt-3 text-zinc-400">
                    We'll be in touch when membership opens.
                  </p>
                </div>
              ) : (
                <form
                  data-testid="membership-waitlist-form"
                  onSubmit={submit}
                  className="space-y-4"
                >
                  <p className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold">
                    Join the waitlist
                  </p>
                  <input
                    data-testid="membership-name-input"
                    type="text"
                    placeholder="Your name (optional)"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full bg-black border border-white/10 px-4 py-3.5 text-white placeholder:text-zinc-600 focus:outline-none focus:border-amber-400 transition-colors"
                  />
                  <input
                    data-testid="membership-email-input"
                    type="email"
                    required
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-black border border-white/10 px-4 py-3.5 text-white placeholder:text-zinc-600 focus:outline-none focus:border-amber-400 transition-colors"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="membership-submit-button"
                    className="w-full inline-flex items-center justify-center gap-3 bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-black px-6 py-4 font-bold uppercase tracking-[0.25em] text-xs transition-colors"
                  >
                    {loading ? "Reserving your spot…" : "Reserve my spot"}
                    <ArrowRight className="h-4 w-4" />
                  </button>
                  <p className="text-xs text-zinc-500 leading-relaxed">
                    Separate from our general mailing list. Membership-only
                    drops and founder pricing.
                  </p>
                </form>
              )}
            </div>
          </FadeIn>
        </div>
      </Container>
    </section>
  );
}
